import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import DepartureActionPills from './DepartureActionPills';
import { AnyRecommendationPending } from './TccPricingProvider';
import { TourRow } from './logic';
import { reviewMoney, rmStatus } from './reviewLogic';
import type { CostBasis, OfferPriority } from './types';

export default function RMReviewTable({
  tours,
  priorities,
  basis,
  open,
  today,
}: {
  tours: Array<TourRow>;
  priorities: Array<OfferPriority>;
  basis: CostBasis;
  open: (id: string) => void;
  today?: string;
}) {
  const columns = ['Tour', 'Review status', 'RM status', '$ at risk', 'Draft score (to validate with RM)'];
  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr 1.5fr 1.5fr' }, gap: 1, p: 1.5 };
  return (
    <Box role="table" aria-label="RM review queue" sx={{ minWidth: 0 }}>
      <Box role="row" sx={{ ...grid, display: { xs: 'none', md: 'grid' }, bgcolor: 'action.hover' }}>
        {columns.map((c) => (
          <Typography role="columnheader" variant="caption" fontWeight={700} key={c}>
            {c}
          </Typography>
        ))}
      </Box>
      {tours.map((t) => {
        const priority = priorities.find((p) => p.opp_id === t.id);
        return (
          <Box
            role="row"
            key={t.id}
            sx={{
              ...grid,
              borderBottom: '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
              '& > *': { minWidth: 0, overflowWrap: 'anywhere' },
            }}
          >
            <Box role="cell">
              <Button onClick={() => open(t.id)} sx={{ p: 0, textAlign: 'left', justifyContent: 'flex-start' }}>
                {t.name}
              </Button>
              <Typography variant="caption" display="block">
                {t.rows.length} departures
              </Typography>
            </Box>
            <Box role="cell">
              <DepartureActionPills rows={t.rows} basis={basis} today={today} />
            </Box>
            <Box role="cell">
              <Typography variant="body2">{[...new Set(t.rows.map(rmStatus))].join(', ')}</Typography>
              <AnyRecommendationPending rows={t.rows} />
            </Box>
            <Box role="cell">
              <Typography variant="caption" display="block">
                Loss exposure {reviewMoney(priority?.loss_exposure)}
              </Typography>
              <Typography variant="caption" display="block">
                Below minimum {reviewMoney(priority?.below_min_gap)}
              </Typography>
            </Box>
            <Box role="cell">
              <Typography variant="caption" sx={{ display: { md: 'none' } }}>
                Draft score (to validate with RM):{' '}
              </Typography>
              {priority?.draft_score?.toFixed(1) ?? 'Information missing'}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
